import { useState, type DragEvent } from 'react';
import { Plus } from 'lucide-react';
import { TASK_STATUSES } from '../../features/tasks/taskModel';
import type { Task, TaskStatus } from '../../features/tasks/types';
import { TaskCard } from './TaskCard';

export interface TaskBoardProps {
  /** Tasks already grouped by status (filtered + sorted upstream). */
  columns: Record<TaskStatus, Task[]>;
  onMove: (task: Task, status: TaskStatus) => void;
  onEdit: (task: Task) => void;
  /** Opens a task's detail page (card title click). */
  onOpen?: (task: Task) => void;
  onDelete: (task: Task) => void;
  /** Opens the create modal preset to a column's status. */
  onAdd: (status: TaskStatus) => void;
}

/**
 * Kanban board: one column per status. Cards are moved between columns by
 * native HTML5 drag-and-drop (drop target highlights on drag-over) or via each
 * card's accessible status select.
 */
export function TaskBoard({
  columns,
  onMove,
  onEdit,
  onOpen,
  onDelete,
  onAdd,
}: TaskBoardProps) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<TaskStatus | null>(null);

  const onDragStart = (e: DragEvent<HTMLElement>, task: Task) => {
    setDraggingId(task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setDragOver(null);
  };

  const onDrop = (e: DragEvent<HTMLElement>, status: TaskStatus) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || draggingId;
    setDragOver(null);
    setDraggingId(null);
    if (!id) return;
    const task = Object.values(columns)
      .flat()
      .find((t) => t.id === id);
    if (task && task.status !== status) onMove(task, status);
  };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
      {TASK_STATUSES.map((col) => {
        const items = columns[col.value] ?? [];
        const isOver = dragOver === col.value;
        return (
          <section
            key={col.value}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (dragOver !== col.value) setDragOver(col.value);
            }}
            onDragLeave={(e) => {
              // Only clear when leaving the column subtree.
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setDragOver((s) => (s === col.value ? null : s));
              }
            }}
            onDrop={(e) => onDrop(e, col.value)}
            className={[
              'flex min-h-[8rem] flex-col rounded-2xl border bg-surface-2/50 p-2 transition-colors',
              isOver ? 'border-accent bg-surface-3/40' : 'border-surface-border',
            ].join(' ')}
          >
            <header className="flex items-center justify-between px-1.5 py-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-300">
                  {col.label}
                </span>
                <span className="rounded-full bg-surface-3 px-1.5 text-xs text-zinc-500">
                  {items.length}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onAdd(col.value)}
                aria-label={`Add task to ${col.label}`}
                className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-zinc-200"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </header>

            <div className="flex flex-1 flex-col gap-2 p-1">
              {items.length === 0 ? (
                <p className="rounded-xl border border-dashed border-surface-border px-3 py-6 text-center text-xs text-zinc-600">
                  Drop tasks here
                </p>
              ) : (
                items.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    dragging={draggingId === task.id}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onEdit={onEdit}
                    onOpen={onOpen}
                    onDelete={onDelete}
                    onMove={onMove}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default TaskBoard;
